// =============================================================================
// FILE:
// frontend/src/modules/pms/task_execution/tabs/MaterialsTab.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  Plus
} from "lucide-react";

import RequestMaterialDrawer
from "../drawers/RequestMaterialDrawer";

import {
  getTaskMaterials
} from "../services/taskExecutionService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function MaterialsTab({

  task

}) {


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [rows, setRows] =
    useState([]);


  const [loading, setLoading] =
    useState(false);


  const [requestDrawerOpen, setRequestDrawerOpen] =
    useState(false);


  const [selectedMaterial, setSelectedMaterial] =
    useState(null);


  // ===========================================================================
  // LOAD MATERIALS
  // ===========================================================================
  useEffect(() => {

    if (task?.id) {

      loadMaterials();
    }

  }, [task]);


  // ===========================================================================
  // LOAD
  // ===========================================================================
  const loadMaterials =
    async () => {

      try {

        setLoading(true);


        // ---------------------------------------------------------------
        // LIVE API
        // ---------------------------------------------------------------
        const response =
          await getTaskMaterials(
            task.id
          );


        // ---------------------------------------------------------------
        // RESPONSE SAFETY
        // ---------------------------------------------------------------
        const payload =
          response?.data || {};


        setRows(
          payload?.data || []
        );

      } catch (err) {

        console.error(
          "Failed to load materials",
          err
        );

      } finally {

        setLoading(false);
      }
    };


  // ===========================================================================
  // REQUEST MATERIAL
  // ===========================================================================
  const handleRequestMaterial =
    (row) => {

      setSelectedMaterial(row);

      setRequestDrawerOpen(true);
    };


  // ===========================================================================
  // REQUEST SUCCESS
  // ===========================================================================
  const handleRequestSuccess =
    async () => {

      await loadMaterials();

      setRequestDrawerOpen(false);

      setSelectedMaterial(null);
    };


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>

        Loading materials...

      </div>
    );
  }


  // ===========================================================================
  // EMPTY
  // ===========================================================================
  if (!rows || rows.length === 0) {

    return (

      <div style={styles.emptyState}>

        No planned materials found

      </div>
    );
  }


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.sectionHeader}>


        <div>

          <div style={styles.title}>

            Task Materials

          </div>


          <div style={styles.subtitle}>


            Material readiness,
            reconciliation,
            request workflow,
            and issue tracking

          </div>

        </div>

      </div>


      {/* ================================================================ */}
      {/* GRID */}
      {/* ================================================================ */}
      <div style={styles.gridContainer}>


        {/* ============================================================ */}
        {/* HEADER */}
        {/* ============================================================ */}
        <div style={styles.headerRow}>


          <div style={styles.colMaterial}>
            Material
          </div>


          <div style={styles.colQty}>
            Planned
          </div>


          <div style={styles.colQty}>
            Reconciled
          </div>


          <div style={styles.colQty}>
            Issued
          </div>


          <div style={styles.colQty}>
            Reserved
          </div>


          <div style={styles.colQty}>
            Missing
          </div>


          <div style={styles.colQty}>
            Requestable
          </div>


          <div style={styles.colStatus}>
            Readiness
          </div>


          <div style={styles.colAction}>
            Action
          </div>

        </div>


        {/* ============================================================ */}
        {/* BODY */}
        {/* ============================================================ */}
        {

          rows.map((row) => (

            <div
              key={row.id}
              style={styles.dataRow}
            >


              {/* ------------------------------------------------------ */}
              {/* MATERIAL */}
              {/* ------------------------------------------------------ */}
              <div style={styles.colMaterial}>


                <div style={styles.materialCode}>


                  {row.material_display || row.material_code}

                </div>


                <div style={styles.materialDesc}>


                  {
                    row.material_name
                  }

                </div>

              </div>


              {/* ------------------------------------------------------ */}
              {/* PLANNED */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.planned_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* RECONCILED */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.reconciled_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* ISSUED */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.issued_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* RESERVED */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.reserved_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* MISSING */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.missing_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* REQUESTABLE */}
              {/* ------------------------------------------------------ */}
              <QtyCell
                value={row.requestable_qty}
              />


              {/* ------------------------------------------------------ */}
              {/* STATUS */}
              {/* ------------------------------------------------------ */}
              <div style={styles.colStatus}>


                <span style={
                  getStatusStyle(
                    row.readiness_status
                  )
                }>

                  {
                    row.readiness_status
                  }

                </span>

              </div>


              {/* ------------------------------------------------------ */}
              {/* ACTION */}
              {/* ------------------------------------------------------ */}
              <div style={styles.colAction}>


                <button
                  style={{
                    ...styles.requestBtn,

                    opacity:
                      row.requestable_qty <= 0
                        ? 0.5
                        : 1,

                    cursor:
                      row.requestable_qty <= 0
                        ? "not-allowed"
                        : "pointer"
                  }}

                  disabled={
                    row.requestable_qty <= 0
                  }

                  onClick={() =>
                    handleRequestMaterial(row)
                  }
                >

                  <Plus size={14} />

                  Request

                </button>

              </div>

            </div>
          ))
        }

      </div>


      {/* ================================================================ */}
      {/* REQUEST DRAWER */}
      {/* ================================================================ */}
      <RequestMaterialDrawer

        open={requestDrawerOpen}

        task={task}

        material={selectedMaterial}

        onClose={() => {

          setRequestDrawerOpen(false);

          setSelectedMaterial(null);
        }}

        onSuccess={handleRequestSuccess}
      />

    </div>
  );
}


// =============================================================================
// QTY CELL
// =============================================================================

function QtyCell({

  value

}) {

  return (

    <div style={styles.colQty}>


      <div style={styles.qtyValue}>


        {value || 0}

      </div>

    </div>
  );
}


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "READY":

      return styles.readyStatus;

    case "PARTIAL":

      return styles.waitingStatus;

    case "BLOCKED":

      return styles.blockedStatus;

    default:

      return styles.defaultStatus;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {

    display: "flex",

    flexDirection: "column"
  },

  sectionHeader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 18,

    marginBottom: 18,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  title: {

    fontSize: 18,

    fontWeight: 700,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280"
  },

  gridContainer: {

    background: "#ffffff",

    borderRadius: 14,

    overflow: "hidden",

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  headerRow: {

    display: "flex",

    background: "#f3f4f6",

    borderBottom:
      "1px solid #e5e7eb",

    padding: "14px 16px",

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  dataRow: {

    display: "flex",

    alignItems: "center",

    padding: "16px",

    borderBottom:
      "1px solid #f3f4f6"
  },

  colMaterial: {

    width: 320
  },

  materialCode: {

    fontSize: 13,

    fontWeight: 700,

    color: "#2563eb"
  },

  materialDesc: {

    fontSize: 13,

    marginTop: 4,

    color: "#374151"
  },

  colQty: {

    width: 120,

    display: "flex",

    justifyContent: "center"
  },

  qtyValue: {

    fontSize: 14,

    fontWeight: 600,

    color: "#111827"
  },

  colStatus: {

    width: 140,

    display: "flex",

    justifyContent: "center"
  },

  colAction: {

    flex: 1,

    display: "flex",

    justifyContent: "center"
  },

  requestBtn: {

    height: 34,

    border: "none",

    background: "#2563eb",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 12px",

    display: "flex",

    alignItems: "center",

    gap: 6,

    cursor: "pointer",

    fontWeight: 600
  },

  readyStatus: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  waitingStatus: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  blockedStatus: {

    background: "#fee2e2",

    color: "#991b1b",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  defaultStatus: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  loader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center"
  },

  emptyState: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center",

    color: "#6b7280"
  }
};